import React, { createContext, useState, useContext, useMemo } from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Typography, 
  Grid,
  Card,
  CardContent,
  CardHeader,
  useMediaQuery,
  IconButton,
  Divider,
  Paper,
  useTheme,
  Tooltip,
  styled,
  AppBar,
  Toolbar,
  alpha,
  Fade,
  createTheme,
  ThemeProvider,
  CssBaseline,
  PaletteMode
} from '@mui/material';
import { 
  WhatsApp as WhatsAppIcon,
  BarChart as BarChartIcon,
  Monitor as MonitorIcon,
  Radio as RadioIcon,
  Assessment as AssessmentIcon,
  BrandingWatermark as BrandingWatermarkIcon,
  Calculate as CalculateIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Dashboard as DashboardIcon,
  ArrowForward as ArrowForwardIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  ArrowDownward as ArrowDownwardIcon,
  GraphicEq as GraphicEqIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

// Create a theme context
type ThemeContextType = {
  mode: PaletteMode;
  toggleColorMode: () => void;
};

const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  toggleColorMode: () => {}
});

// Create ThemeProvider component
const AppThemeProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [mode, setMode] = useState<PaletteMode>('light');
  
  const toggleColorMode = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };
  
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
        },
      }),
    [mode],
  );
  
  const contextValue = useMemo(
    () => ({
      mode,
      toggleColorMode,
    }),
    [mode]
  );
  
  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

// Styled Card component with hover effect - matching Dashboard style
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[8],
  },
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius * 1.5,
  overflow: 'hidden'
}));

// Custom icon background for better visibility
const IconBackground = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.12),
  borderRadius: '50%',
  padding: theme.spacing(1.5),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: theme.palette.primary.main
}));

// Modern button with hover effect
const ModernButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(1.2, 3),
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: theme.shadows[2],
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  }
})) as typeof Button;

// Animated scroll indicator
const ScrollIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 40,
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  color: theme.palette.primary.contrastText,
  opacity: 0.8,
  transition: 'opacity 0.3s',
  cursor: 'pointer',
  '&:hover': {
    opacity: 1
  },
  zIndex: 5,
  animation: 'bounce 2s infinite'
}));

// Gradient overlay for footer
const GradientOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '100%',
  background: theme.palette.mode === 'dark' 
    ? `linear-gradient(to bottom, ${alpha(theme.palette.background.paper, 0.2)}, ${alpha(theme.palette.background.paper, 0.8)})`
    : `linear-gradient(to bottom, ${alpha(theme.palette.primary.dark, 0.8)}, ${alpha(theme.palette.primary.dark, 0.95)})`,
  zIndex: 0
}));

// Global CSS for animations
const GlobalStyles = () => {
  return (
    <style>
      {`
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.8; }
          100% { transform: scale(1); opacity: 1; }
        }
        
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-10px); }
          60% { transform: translateY(-5px); }
        }
      `}
    </style>
  );
};

const Home: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';
  
  // Use the theme context
  const { toggleColorMode } = useContext(ThemeContext);
  
  const whatsappNumber = "+5548988553212";
  const whatsappMessage = "Olá! Gostaria de mais informações sobre o CCPMIDIA Dashboard.";
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

  const scrollToContent = () => {
    const contentElement = document.getElementById('content-section');
    if (contentElement) {
      contentElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    {
      icon: <MonitorIcon fontSize="large" />,
      title: "Monitoramento de TV e Rádio",
      description: "Acompanhe em tempo real a veiculação de anúncios em emissoras de TV e rádio."
    },
    {
      icon: <BrandingWatermarkIcon fontSize="large" />,
      title: "Gestão de Anúncios",
      description: "Registre, categorize e analise todos os anúncios transmitidos em diferentes canais."
    },
    {
      icon: <AssessmentIcon fontSize="large" />,
      title: "Relatórios Detalhados",
      description: "Gere relatórios completos e visualize dados de forma clara e objetiva."
    },
    {
      icon: <RadioIcon fontSize="large" />,
      title: "Gestão de Emissoras",
      description: "Administre todas as emissoras monitoradas em uma única plataforma."
    },
    {
      icon: <BarChartIcon fontSize="large" />,
      title: "Categorização Inteligente",
      description: "Organize marcas, anunciantes e agências com uma estrutura hierárquica eficiente."
    },
    {
      icon: <CalculateIcon fontSize="large" />,
      title: "Análise de Custos",
      description: "Calcule métricas e custos de campanhas publicitárias com precisão."
    },
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column', 
      bgcolor: 'background.default' 
    }}>
      <GlobalStyles />
      
      {/* Header */}
      <AppBar position="fixed" elevation={0} color="transparent" sx={{ 
        backdropFilter: 'blur(8px)',
        backgroundColor: alpha(theme.palette.background.default, 0.8),
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`
      }}>
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box 
                sx={{ 
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  p: 1, 
                  borderRadius: '50%',
                  mr: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <GraphicEqIcon sx={{ color: 'primary.main', fontSize: 24 }} />
              </Box>
              <Typography
                variant="h5"
                component="div"
                sx={{
                  fontWeight: 700,
                  color: 'primary.main',
                  letterSpacing: '.05rem',
                  mr: 1,
                  display: { xs: 'none', md: 'flex' }
                }}
              >
                CCPMIDIA
              </Typography>
              <Typography
                variant="h5"
                component="div"
                sx={{
                  fontWeight: 500,
                  color: 'text.primary',
                  display: { xs: 'none', md: 'flex' }
                }}
              >
                Dashboard
              </Typography>
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: 700,
                  color: 'primary.main',
                  letterSpacing: '.05rem',
                  display: { xs: 'flex', md: 'none' }
                }}
              >
                CCPMIDIA
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Dark Mode Toggle */}
              <Tooltip title={isDark ? "Alternar para modo claro" : "Alternar para modo escuro"}>
                <IconButton 
                  onClick={toggleColorMode}
                  color="primary"
                  sx={{ 
                    p: 1.2,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.15),
                    },
                    transition: 'all 0.2s'
                  }}
                >
                  {isDark ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
              </Tooltip>
              
              <Button 
                component={Link} 
                to="/login" 
                variant="outlined" 
                color="primary" 
                sx={{ 
                  mr: { xs: 1, md: 2 },
                  borderRadius: theme.shape.borderRadius * 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderWidth: 1.5,
                  '&:hover': {
                    borderWidth: 1.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.04)
                  }
                }}
              >
                Login
              </Button>
              <ModernButton
                variant="contained"
                color="primary"
                href={whatsappLink}
                component="a"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<WhatsAppIcon />}
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              >
                Fale com um Especialista
              </ModernButton>
              <IconButton
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                sx={{ 
                  display: { xs: 'inline-flex', sm: 'none' },
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                  }
                }}
              >
                <WhatsAppIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Toolbar offset for fixed AppBar */}
      <Toolbar />

      {/* Hero Section with Enhanced Design */}
      <Box
        sx={{
          position: 'relative',
          color: 'primary.contrastText',
          py: { xs: 12, md: 20 },
          overflow: 'hidden',
          minHeight: '85vh',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        {/* Background gradient */}
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          bgcolor: 'primary.main',
          zIndex: -2
        }} />
        
        {/* Background pattern */}
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          opacity: isDark ? 0.05 : 0.1,
          backgroundImage: isDark 
            ? 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'white\' fill-opacity=\'1\' fill-rule=\'evenodd\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/svg%3E")'
            : 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23102A43\' fill-opacity=\'1\' fill-rule=\'evenodd\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/svg%3E")',
          zIndex: -1
        }} />
        
        <Container maxWidth="lg">
          <Fade in={true} timeout={1000}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={9} sx={{ position: 'relative', zIndex: 2 }}>
                <Typography
                  variant={isMobile ? 'h3' : 'h1'}
                  component="h1"
                  sx={{ 
                    fontWeight: 800,
                    mb: 3,
                    lineHeight: 1.1,
                    letterSpacing: '-0.02em',
                    fontSize: { xs: '2.5rem', md: '4rem' },
                    background: isDark 
                      ? 'linear-gradient(90deg, #fff 0%, #e0e0e0 100%)' 
                      : 'linear-gradient(90deg, #102A43 0%, #243B53 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    textShadow: isDark 
                      ? '0 4px 30px rgba(0,0,0,0.1)'
                      : '0 2px 10px rgba(0,0,0,0.06)'
                  }}
                >
                  Monitoramento Inteligente de Mídia
                </Typography>
                <Typography
                  variant="h6"
                  component="p"
                  sx={{ 
                    mb: 5,
                    fontWeight: 400,
                    maxWidth: '90%',
                    fontSize: { xs: '1rem', md: '1.25rem' },
                    color: isDark
                      ? alpha('#ffffff', 0.9)
                      : '#102A43',
                    textShadow: isDark
                      ? '0 2px 10px rgba(0,0,0,0.1)'
                      : 'none',
                    lineHeight: 1.6
                  }}
                >
                  Acompanhe, analise e otimize a veiculação de anúncios em TV e rádio com precisão e eficiência.
                  Nossa plataforma oferece insights valiosos para maximizar o retorno do seu investimento em mídia.
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 4 }}>
                  <ModernButton
                    variant="contained"
                    color="secondary"
                    size="large"
                    href={whatsappLink}
                    component="a"
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<WhatsAppIcon />}
                    sx={{ 
                      py: 1.5,
                      px: 4,
                      fontSize: '1.1rem'
                    }}
                  >
                    Fale com um Especialista
                  </ModernButton>
                  <ModernButton
                    variant="outlined"
                    component={Link}
                    to="/login"
                    size="large"
                    sx={{ 
                      py: 1.5,
                      px: 4,
                      fontSize: '1.1rem',
                      color: isDark ? '#fff' : '#102A43',
                      backgroundColor: isDark ? alpha(theme.palette.primary.main, 0.15) : alpha('#ffffff', 0.25),
                      borderColor: isDark ? theme.palette.primary.main : '#102A43',
                      borderWidth: 2,
                      '&:hover': {
                        backgroundColor: isDark ? alpha(theme.palette.primary.main, 0.25) : alpha('#ffffff', 0.4),
                        borderColor: isDark ? theme.palette.primary.light : '#102A43',
                        borderWidth: 2,
                      }
                    }}
                  >
                    Acessar Dashboard
                  </ModernButton>
                </Box>
              </Grid>
            </Grid>
          </Fade>
        </Container>

        {/* Background decorative elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '5%',
            right: '-10%',
            width: '500px',
            height: '500px',
            borderRadius: '50%',
            background: isDark
              ? `radial-gradient(circle, ${alpha(theme.palette.primary.dark, 0.8)} 0%, ${alpha(theme.palette.primary.main, 0)} 70%)`
              : `radial-gradient(circle, ${alpha(theme.palette.primary.light, 0.3)} 0%, ${alpha(theme.palette.primary.light, 0)} 70%)`,
            zIndex: 0,
            animation: 'pulse 8s infinite'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '-15%',
            left: '-10%',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: isDark
              ? `radial-gradient(circle, ${alpha(theme.palette.primary.dark, 0.8)} 0%, ${alpha(theme.palette.primary.main, 0)} 70%)`
              : `radial-gradient(circle, ${alpha(theme.palette.primary.light, 0.3)} 0%, ${alpha(theme.palette.primary.light, 0)} 70%)`,
            zIndex: 0,
            animation: 'pulse 12s infinite'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '40%',
            right: '30%',
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: isDark
              ? `radial-gradient(circle, ${alpha(theme.palette.primary.light, 0.3)} 0%, ${alpha(theme.palette.primary.light, 0)} 70%)`
              : `radial-gradient(circle, ${alpha(theme.palette.primary.light, 0.5)} 0%, ${alpha(theme.palette.primary.light, 0)} 70%)`,
            zIndex: 0,
            animation: 'pulse 6s infinite'
          }}
        />
        
        {/* Scroll indicator */}
        <ScrollIndicator onClick={scrollToContent}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            Saiba mais
          </Typography>
          <ArrowDownwardIcon sx={{ 
            fontSize: 24,
            animation: 'bounce 2s infinite'
          }} />
        </ScrollIndicator>
      </Box>

      {/* O que é o CCPMIDIA Dashboard */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }} id="content-section">
        <Box textAlign="center" mb={8}>
          <Typography
            variant="h3"
            component="h2"
            sx={{ 
              fontWeight: 700, 
              mb: 3,
              color: 'text.primary',
              letterSpacing: '-0.01em'
            }}
          >
            O que é o CCPMIDIA Dashboard
          </Typography>
          <Typography
            variant="h6"
            component="p"
            sx={{ 
              maxWidth: 800, 
              mx: 'auto',
              color: 'text.secondary',
              lineHeight: 1.6,
              fontWeight: 400
            }}
          >
            O CCPMIDIA Dashboard é uma plataforma completa para monitoramento de veiculação de anúncios em streams de TV e Rádio. 
            Desenvolvido para agências de publicidade, anunciantes e profissionais de mídia, nosso sistema oferece análise em tempo real, 
            categorização inteligente e relatórios detalhados para otimizar suas estratégias de mídia.
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 8 }} />
        
        {/* O que ele faz */}
        <Typography
          variant="h3"
          component="h2"
          sx={{ 
            fontWeight: 700, 
            mb: 5,
            textAlign: 'center',
            color: 'text.primary',
            letterSpacing: '-0.01em'
          }}
        >
          O que ele faz
        </Typography>
        
        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <StyledCard elevation={isDark ? 2 : 1}>
                <CardHeader
                  avatar={
                    <IconBackground>
                      {feature.icon}
                    </IconBackground>
                  }
                  title={feature.title}
                  titleTypographyProps={{ 
                    variant: 'h6',
                    sx: { fontWeight: 600, color: 'text.primary' }
                  }}
                />
                <CardContent sx={{ flexGrow: 1, pt: 0 }}>
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </StyledCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ 
        py: { xs: 8, md: 10 },
        bgcolor: alpha(theme.palette.primary.main, isDark ? 0.2 : 0.05)
      }}>
        <Container maxWidth="md">
          <Paper 
            elevation={isDark ? 3 : 1} 
            sx={{ 
              p: { xs: 3, md: 5 }, 
              borderRadius: theme.shape.borderRadius * 2,
              bgcolor: 'background.paper',
              textAlign: 'center',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            {/* Decorative element */}
            <Box 
              sx={{ 
                position: 'absolute', 
                top: -30, 
                right: -30, 
                width: 150, 
                height: 150, 
                borderRadius: '50%', 
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                zIndex: 0
              }} 
            />
            
            <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 2, md: 4 } }}>
              <Typography
                variant="h4"
                component="h2"
                sx={{ 
                  fontWeight: 700, 
                  mb: 2,
                  color: 'text.primary'
                }}
              >
                Tenho interesse, falar com um especialista
              </Typography>
              <Typography
                variant="body1"
                sx={{ 
                  mb: 4,
                  color: 'text.secondary',
                  maxWidth: 600,
                  mx: 'auto'
                }}
              >
                Quer saber mais sobre como o CCPMIDIA Dashboard pode transformar seu monitoramento de mídia? 
                Nossos especialistas estão prontos para ajudar e mostrar como nossa solução pode atender às suas necessidades.
              </Typography>
              <ModernButton
                variant="contained"
                color="primary"
                size="large"
                href={whatsappLink}
                component="a"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<WhatsAppIcon />}
                endIcon={<ArrowForwardIcon />}
                sx={{ py: 1.5, px: 4 }}
              >
                Falar com um Especialista no WhatsApp
              </ModernButton>
              <Typography
                variant="body2"
                sx={{ 
                  mt: 2,
                  color: 'text.secondary',
                  fontWeight: 500
                }}
              >
                WhatsApp: +55 48 98855-3212
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Enhanced Footer with background image */}
      <Box 
        component="footer"
        sx={{ 
          position: 'relative',
          py: 6,
          mt: 'auto',
          color: isDark ? 'text.primary' : 'primary.contrastText',
          overflow: 'hidden'
        }}
      >
        {/* Background image */}
        <Box 
          sx={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: isDark 
              ? 'url("https://images.unsplash.com/photo-1557682250-33bd709cbe85?q=80&w=2940")'
              : 'url("https://images.unsplash.com/photo-1557682260-96655317b5e6?q=80&w=2830")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: -2,
            opacity: isDark ? 0.1 : 0.8
          }}
        />
        
        {/* Gradient overlay */}
        <GradientOverlay />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box 
                  sx={{ 
                    mr: 1.5, 
                    bgcolor: isDark 
                      ? alpha(theme.palette.primary.main, 0.2)
                      : alpha('#ffffff', 0.2),
                    p: 1, 
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <DashboardIcon sx={{ 
                    fontSize: 24, 
                    color: isDark ? 'primary.main' : 'primary.contrastText' 
                  }} />
                </Box>
                <Typography variant="h5" component="div" sx={{ fontWeight: 700 }}>
                  CCPMIDIA Dashboard
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ mb: 3, opacity: 0.9, maxWidth: 300 }}>
                A solução completa para monitoramento e análise de veiculações publicitárias em TV e rádio.
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                © {new Date().getFullYear()} CCPMIDIA. Todos os direitos reservados.
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Navegação Rápida
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Button 
                  component={Link} 
                  to="/login" 
                  color="inherit" 
                  sx={{ 
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': { 
                      bgcolor: alpha(theme.palette.primary.contrastText, 0.1),
                      pl: 2
                    },
                    transition: 'all 0.2s'
                  }}
                >
                  Dashboard
                </Button>
                <Button 
                  component="a"
                  href="#content-section"
                  color="inherit" 
                  sx={{ 
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': { 
                      bgcolor: alpha(theme.palette.primary.contrastText, 0.1),
                      pl: 2
                    },
                    transition: 'all 0.2s'
                  }}
                >
                  Recursos
                </Button>
                <Button 
                  component="a"
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="inherit" 
                  sx={{ 
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': { 
                      bgcolor: alpha(theme.palette.primary.contrastText, 0.1),
                      pl: 2
                    },
                    transition: 'all 0.2s'
                  }}
                >
                  Contato
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={5}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Entre em contato
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <ModernButton
                  variant={isDark ? 'outlined' : 'contained'}
                  color={isDark ? 'primary' : 'secondary'}
                  href={whatsappLink}
                  component="a"
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<WhatsAppIcon />}
                  sx={{ 
                    borderColor: isDark ? 'primary.main' : 'transparent',
                    color: isDark ? 'primary.main' : 'primary.contrastText',
                  }}
                >
                  WhatsApp
                </ModernButton>
                <ModernButton
                  variant="outlined"
                  component={Link}
                  to="/login"
                  sx={{ 
                    color: isDark ? 'text.primary' : 'primary.contrastText',
                    borderColor: isDark ? alpha(theme.palette.text.primary, 0.5) : alpha(theme.palette.primary.contrastText, 0.5),
                    '&:hover': {
                      borderColor: isDark ? theme.palette.text.primary : theme.palette.primary.contrastText,
                      bgcolor: isDark ? alpha(theme.palette.text.primary, 0.08) : alpha(theme.palette.primary.contrastText, 0.08)
                    }
                  }}
                >
                  Acessar Dashboard
                </ModernButton>
              </Box>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 4 }}>
                <Button 
                  color="inherit" 
                  variant="text"
                  sx={{ 
                    '&:hover': { 
                      bgcolor: alpha(theme.palette.primary.contrastText, 0.1) 
                    },
                    fontWeight: 500,
                    textTransform: 'none',
                    borderRadius: theme.shape.borderRadius * 2,
                    fontSize: '0.875rem'
                  }}
                >
                  Política de Privacidade
                </Button>
                <Button 
                  color="inherit" 
                  variant="text"
                  sx={{ 
                    '&:hover': { 
                      bgcolor: alpha(theme.palette.primary.contrastText, 0.1) 
                    },
                    fontWeight: 500,
                    textTransform: 'none',
                    borderRadius: theme.shape.borderRadius * 2,
                    fontSize: '0.875rem'
                  }}
                >
                  Termos de Uso
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

// Wrap the Home component with the ThemeProvider
const HomeWithTheme: React.FC = () => {
  return (
    <AppThemeProvider>
      <Home />
    </AppThemeProvider>
  );
};

export default HomeWithTheme;
